import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Rocket, UserPlus, Calendar, Users, FileText, CreditCard, CheckCircle2, ArrowRight, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { key: 'profile_complete', label: 'Complete Your Profile', desc: 'Fill in your organization details and personal information', icon: User, link: '/settings', action: 'Go to Settings' },
  { key: 'team_invited', label: 'Invite Your Team', desc: 'Send invitations to caregivers, admins, and staff members', icon: UserPlus, link: '/bulk-invite', action: 'Invite Team' },
  { key: 'first_client_added', label: 'Add Your First Client', desc: 'Register a client to begin providing care services', icon: Users, link: '/dashboard/admin/clients', action: 'Add Client' },
  { key: 'first_shift_created', label: 'Create Your First Shift', desc: 'Schedule a care shift for one of your clients', icon: Calendar, link: '/dashboard/admin/shifts', action: 'Create Shift' },
  { key: 'documents_uploaded', label: 'Upload Documents', desc: 'Upload compliance documents, policies, and certificates', icon: FileText, link: '/document-storage', action: 'Upload Documents' },
  { key: 'billing_setup', label: 'Set Up Billing', desc: 'Configure your subscription and payment method', icon: CreditCard, link: '/subscription', action: 'Set Up Billing' },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOnboarding(); }, []);

  const fetchOnboarding = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/onboarding/status`, { withCredentials: true });
      setOnboarding(data);
    } catch { toast.error('Failed to load onboarding status'); }
    setLoading(false);
  };

  const steps = onboarding?.steps || {};
  const completed = onboarding?.completed || 0;
  const total = onboarding?.total || STEPS.length;
  const progress = Math.round((completed / total) * 100);
  const allDone = completed === total;

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><div className="spinner w-8 h-8" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl" data-testid="onboarding-wizard-page">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-2xl bg-[#00A6A0]/10">
            <Rocket className="w-7 h-7 text-[#00A6A0]" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              {allDone ? 'Setup Complete!' : 'Getting Started'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {allDone
                ? 'Congratulations! Your agency is fully set up and ready to go.'
                : 'Complete these steps to set up your agency on FendanaCura'}
            </p>
          </div>
        </div>

        {/* Progress */}
        <Card className="glass-card border-border" data-testid="onboarding-progress">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-foreground">Setup Progress</span>
              <span className="text-sm font-bold text-[#00A6A0]">{completed}/{total} steps</span>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#00A6A0] to-emerald-400 rounded-full transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{progress}% complete</p>
          </CardContent>
        </Card>

        {/* Steps */}
        <div className="space-y-3">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isDone = steps[step.key];
            return (
              <Card
                key={step.key}
                className={`border transition-all ${isDone ? 'border-[#00A6A0]/30 bg-[#00A6A0]/5' : 'border-border glass-card'}`}
                data-testid={`onboarding-step-${step.key}`}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      isDone ? 'bg-[#00A6A0] text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {isDone ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${isDone ? 'text-[#00A6A0]' : 'text-muted-foreground'}`} />
                      <p className={`text-sm font-medium ${isDone ? 'text-[#00A6A0] line-through' : 'text-foreground'}`}>{step.label}</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                  </div>
                  {!isDone && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#00A6A0]/30 text-[#00A6A0] hover:bg-[#00A6A0]/10 flex-shrink-0"
                      onClick={() => navigate(step.link)}
                      data-testid={`onboarding-action-${step.key}`}
                    >
                      {step.action} <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
